import {logger, ObjUtil, Time, Util, UUID} from 'yougile-util'

export type DataObjectId = UUID & {__subtype: 'DataObjectId'}

export const createDataObjectId = (): DataObjectId =>
  Util.UUID() as DataObjectId

export type DataObjectPropertiesValue = number | string | boolean | object

export interface DataObjectProperties {
  [key: string]: DataObjectPropertiesValue
}

export type DataObjectList<T = any> = {[id: DataObjectId]: T}

export const isDataObjectId = (id: unknown): id is DataObjectId =>
  typeof id === 'string' && Util.isUUID(id)

export type DataObjectTypeName =
  | 'Connections'
  | 'User'
  | 'Hub'
  | 'Tag'
  | 'Chat'
  | 'ChatMessage'
  | ''

export interface DataObjectJSON {
  dataType: DataObjectTypeName
  id: DataObjectId
  timestamp: number
  revision?: number
  deleted?: boolean
  properties?: DataObjectProperties
  isLazy?: boolean
}

export const dataObjectDefaultValues = {
  deleted: false,
  dataType: '',
  revision: 1,
  isLazy: true
} as const

export abstract class DataObject {
  /**
   * Every data object scope should be restricted to some sandbox, that is
   * identified by companyId.
   * This field is not stored in DataObjectJSON.
   */
  public companyId: DataObjectId

  public dataType!: DataObjectTypeName
  public revision!: number

  public deleted!: boolean
  public timestamp: number
  public attached?: boolean
  public properties: DataObjectProperties = {}
  public updateTime: number

  public isLazy!: boolean

  private commitHandler?: (obj: DataObject, forced: boolean) => void

  private realId?: DataObjectId

  get id(): DataObjectId {
    if (!this.realId) {
      this.realId = Util.UUID() as DataObjectId
    }
    return this.realId
  }

  set id(value: DataObjectId) {
    this.realId = value
  }

  protected constructor(companyId: DataObjectId) {
    const now = Time.now()
    this.companyId = companyId
    this.timestamp = now
    this.updateTime = now

    let lastProto = Object.getPrototypeOf(this)
    while (
      lastProto.__proto__ &&
      lastProto.__proto__ !== Object.prototype &&
      lastProto.__proto__ !== dataObjectDefaultValues
    ) {
      lastProto = lastProto.__proto__
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    lastProto.__proto__ = dataObjectDefaultValues
  }

  public toJSON(): DataObjectJSON {
    if (!this.dataType) {
      throw new TypeError('dataType field should be set')
    }
    const res: DataObjectJSON = {
      id: this.id,
      timestamp: this.timestamp,
      dataType: this.dataType
    }
    if (this.revision > 1) {
      res.revision = this.revision
    }
    if (this.deleted) {
      res.deleted = true
    }
    if (!ObjUtil.isEmpty(this.properties)) {
      res.properties = Util.clone(this.properties ?? {})
    }
    return res
  }

  public fromJSON(
    companyId: DataObjectId,
    json: DataObjectJSON | undefined
  ): DataObject | undefined {
    if (!json) {
      logger.error('no json', json)
      return undefined
    }
    this.companyId = companyId
    this.id = json.id
    if (json.dataType) {
      this.dataType = json.dataType
    }
    this.timestamp = +json.timestamp
    if (json.revision) {
      this.revision = +(json.revision || 1)
    }
    this.deleted = !!json.deleted
    if (json.properties) {
      this.properties = Util.clone(json.properties) ?? {}
    }
    if (typeof json.isLazy === 'boolean') {
      if (json.isLazy !== this.isLazy) {
        this.isLazy = json.isLazy
      }
    }
    if (typeof json.isLazy === 'undefined') {
      this.isLazy = false
    }
    this.updateTime = Time.now()

    return this
  }

  public commit(force = false): void {
    if (this.isLazy) {
      logger.error('Tried to commit lazy object', this.id)
      throw new Error('Tried to commit lazy object')
    }
    if (typeof this.commitHandler === 'function') {
      this.commitHandler(this, force)
      this.updateTime = Time.now()
    } else {
      logger.warn(
        'Tried to commit DataObject that has not commit handler yet',
        new Error().stack
      )
    }
  }

  public onCommit(handler: (obj: DataObject, forced: boolean) => void): void {
    if (typeof this.commitHandler === 'function') {
      throw new Error(
        'Reassigning commit handler is forbidden unless specified explicitly'
      )
    } else {
      this.commitHandler = handler
      this.attached = true
    }
  }

  public removeOnCommit(): void {
    this.commitHandler = undefined
    this.attached = false
  }
}
